/**
 * PaymentPlansTab.js
 * ==================
 *
 * Staff-only management UI for monthly payment plans.
 *
 * Why this tab exists
 * -------------------
 * The Plans tab is the schedule layer that sits on top of the existing
 * Invoices tab. Each plan emits one regular ``PatientInvoice`` per
 * installment that flows through the same surcharge / debit-override /
 * webhook plumbing as a one-off charge — so the Invoice tab keeps
 * working unchanged. This tab adds three things the Invoice tab can't
 * cleanly express on its own:
 *
 *   1. Lifecycle controls — pause / resume / cancel / update card /
 *      charge now, all of which mutate the ``PatientPaymentPlans``
 *      doc, not individual invoices.
 *   2. A "needs attention" view — the count + filter for ``defaulted``
 *      plans is the dashboard alert mechanism (in lieu of a separate
 *      Notifications container).
 *   3. Plan-aware progress — # installments paid / remaining, total
 *      charged so far, the next charge date, and the plan-specific
 *      activity log. None of that lives on invoices individually.
 *
 * Layout
 * ------
 * Self-contained: status filter pills (with red defaulted-count badge),
 * "+ New plan" button, and a single table. Clicking a row opens a
 * detail drawer with the schedule, activity log, side-loaded invoices,
 * and the lifecycle action buttons.
 *
 * Styling: inline styles to match the rest of the payments frontend
 * (InvoiceTab / TransactionsTab) rather than introducing Tailwind in
 * this app. Keeps the diff focused and ships zero new build deps.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchFromPaymentsAPI, parseBackendDate } from '../services/common.js';
import StaffAddCardForm from './StaffAddCardForm.js';
import Icon from './Icon';
import {
    listPaymentPlans,
    getPaymentPlan,
    createPaymentPlan,
    pausePaymentPlan,
    resumePaymentPlan,
    cancelPaymentPlan,
    chargePaymentPlanNow,
    updatePaymentPlanCard,
    listPatientPaymentMethods,
} from '../services/paymentsService.js';

// --- formatters --------------------------------------------------------------

const formatCurrencyCents = (cents) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
        .format((cents || 0) / 100);

const formatDate = (s) => {
    const d = parseBackendDate(s);
    if (!d) return '—';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Human-readable relative date for "next charge" hints: "today",
// "tomorrow", "in 3 days", "yesterday", "3 days ago". Compared at
// local-midnight precision so a charge dated today never reads as
// "in 0 days" because of a few hours of clock drift, and a date the
// user picked yesterday doesn't accidentally render as "today".
// Returns ``null`` when the input is unparseable so callers can
// render their fallback ("—" / nothing).
const formatRelativeDate = (s) => {
    const d = parseBackendDate(s);
    if (!d) return null;
    const startOfDay = (x) => {
        const c = new Date(x);
        c.setHours(0, 0, 0, 0);
        return c;
    };
    const target = startOfDay(d);
    const today = startOfDay(new Date());
    const diffDays = Math.round((target - today) / 86400000);
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'tomorrow';
    if (diffDays === -1) return 'yesterday';
    if (diffDays > 0) return `in ${diffDays} days`;
    return `${Math.abs(diffDays)} days ago`;
};

const formatDateTime = (s) => {
    const d = parseBackendDate(s);
    if (!d) return '—';
    return d.toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
    });
};

// Status pill colors — kept aligned with the invoice status palette so a
// staffer's pattern recognition transfers between tabs.
const STATUS_STYLE = {
    active:    { bg: '#e7f4ec', color: '#1d6b40', label: 'Active' },
    paused:    { bg: '#fdf3e3', color: '#a8520c', label: 'Paused' },
    completed: { bg: '#e8f0f7', color: '#1f4e79', label: 'Completed' },
    cancelled: { bg: '#e3e7e4', color: '#3c4540', label: 'Cancelled' },
    defaulted: { bg: '#fdecea', color: '#912018', label: 'Defaulted' },
};
const statusStyle = (s) => STATUS_STYLE[s] || { bg: '#eef0ee', color: '#3c4540', label: s || '—' };

// ----------------------------------------------------------------------------
// Helper: load practice payments config so the enrollment modal can mention
// surcharge behavior. We only display, never modify, so a partial fetch is
// fine — the cron + charge layer reads this field live anyway.
//
// Response shape (see ``get_practice_info`` in server.py):
//   { success, practice: { practiceId, name, timezone, payments: { surchargeEnabled, ... } } }
// ----------------------------------------------------------------------------
async function loadPracticePaymentsConfig(practiceId) {
    try {
        const info = await fetchFromPaymentsAPI(
            `/api/payments/practices/${encodeURIComponent(practiceId)}/info?practiceId=${encodeURIComponent(practiceId)}`,
        );
        return info?.practice?.payments || {};
    } catch (e) {
        console.warn('[PaymentPlansTab] failed to load practice payments cfg:', e);
        return {};
    }
}

// ----------------------------------------------------------------------------
// Surcharge preview — frontend mirror of payment_methods.compute_surcharge.
//
// Same rules: only CREDIT cards are surchargeable; debit / prepaid / unknown
// pay base. The displayed pct is the practice's configured cap (clamped to
// 3% across all networks, matching ``_brand_pct_cap`` today). Floors to
// whole cents to match the backend's integer math.
//
// We deliberately compute this client-side (rather than calling a preview
// endpoint per amount-edit) because:
//   1. The actual charge at cron time uses the backend's full logic with
//      live practice + card data, so this UI value is only ever a forecast.
//   2. We want the value to update instantly as the staffer types the
//      installment amount or picks a different card.
//
// Returns ``null`` for "no surcharge applies"; otherwise ``{pct, totalCents,
// surchargeCents}``. Caller decides how to render.
// ----------------------------------------------------------------------------
function computeSurchargePreview(baseCents, card, practiceCfg) {
    if (!practiceCfg?.surchargeEnabled) return null;
    if (!Number.isFinite(baseCents) || baseCents <= 0) return null;
    if (!card) return null;
    const cardInner = card.card || card;
    const fundingType = (cardInner.type || '').toUpperCase();
    if (fundingType !== 'CREDIT') return null;
    const brand = (cardInner.brand || '').toUpperCase();
    // Match the backend's brand allowlist (`_BRAND_DENYLIST` excludes
    // UNKNOWN). Surcharge requires a confidently-classified network.
    if (!brand || brand === 'UNKNOWN') return null;
    // Practices need a registered state to surcharge legally; without it
    // the backend refuses (defence-in-depth against a half-filled config).
    if (!practiceCfg.surchargeRegisteredState) return null;

    const configuredPct = Number.isFinite(parseFloat(practiceCfg.surchargePctCap))
        ? parseFloat(practiceCfg.surchargePctCap)
        : 3.0;
    const pct = Math.min(Math.max(configuredPct, 0.0), 3.0);  // brand cap = 3% today
    if (pct <= 0) return null;

    const surchargeCents = Math.floor((baseCents * pct) / 100);
    if (surchargeCents <= 0) return null;
    return {
        pct,
        surchargeCents,
        totalCents: baseCents + surchargeCents,
    };
}

// ============================================================================
// Main tab component
// ============================================================================

const PaymentPlansTab = ({ practiceId }) => {
    // 'all' = no filter; otherwise the literal status string sent to the
    // backend's ``status=`` query param. Staff almost always lands here to
    // triage active + defaulted, so we default to 'all' (which sorts
    // defaulted to the top via the count badge) rather than hiding completed.
    const [statusFilter, setStatusFilter] = useState('all');
    // Client-side patient search over the loaded list — same matching as the
    // Invoices tab. Sticky across status pills, like the invoice filter.
    const [planFilter, setPlanFilter] = useState('');
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [toast, setToast] = useState(null); // { type: 'success'|'error', message }

    const [selectedPlan, setSelectedPlan] = useState(null); // full detail object
    const [showDetail, setShowDetail] = useState(false);
    const [showEnroll, setShowEnroll] = useState(false);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3500);
    };

    const loadPlans = useCallback(async () => {
        if (!practiceId) return;
        setLoading(true);
        setErrorMsg('');
        try {
            const opts = {};
            if (statusFilter !== 'all') opts.statuses = [statusFilter];
            const resp = await listPaymentPlans(practiceId, opts);
            if (resp?.success) {
                setPlans(resp.data || []);
            } else {
                setErrorMsg(resp?.error || 'Failed to load plans');
                setPlans([]);
            }
        } catch (e) {
            console.error('[PaymentPlansTab] load error:', e);
            setErrorMsg(String(e?.message || e));
            setPlans([]);
        } finally {
            setLoading(false);
        }
    }, [practiceId, statusFilter]);

    useEffect(() => { loadPlans(); }, [loadPlans]);

    // Defaulted-count badge — load the unfiltered set once so the badge
    // accurately reflects the practice-wide count even when the user is
    // viewing only 'completed' / 'cancelled'. Cheap query (typical
    // practice has < 100 plans total).
    const [defaultedCount, setDefaultedCount] = useState(0);
    useEffect(() => {
        let aborted = false;
        (async () => {
            if (!practiceId) return;
            try {
                const resp = await listPaymentPlans(practiceId, { statuses: ['defaulted'] });
                if (!aborted && resp?.success) setDefaultedCount((resp.data || []).length);
            } catch (_) { /* badge is best-effort */ }
        })();
        return () => { aborted = true; };
    }, [practiceId, plans /* recompute after any list mutation */]);

    const openDetail = async (planId) => {
        try {
            const resp = await getPaymentPlan(practiceId, planId);
            if (resp?.success) {
                setSelectedPlan({ plan: resp.data, invoices: resp.invoices || [] });
                setShowDetail(true);
            } else {
                showToast(resp?.error || 'Failed to load plan', 'error');
            }
        } catch (e) {
            showToast(String(e?.message || e), 'error');
        }
    };

    const refreshDetail = async () => {
        if (!selectedPlan?.plan?.id) return;
        const resp = await getPaymentPlan(practiceId, selectedPlan.plan.id);
        if (resp?.success) {
            setSelectedPlan({ plan: resp.data, invoices: resp.invoices || [] });
        }
    };

    const trimmedFilter = planFilter.trim().toLowerCase();
    // Phone matches compare digits only, so "5551234" finds "(555) 123-4…".
    const filterDigits = trimmedFilter.replace(/\D/g, '');
    const matchesFilter = (plan) => {
        if (!trimmedFilter) return true;
        const haystackText = [plan.patientName, plan.patientEmail, plan.patientId]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
        if (haystackText.includes(trimmedFilter)) return true;
        // Needs 3+ digits so a stray "1" doesn't match every phone.
        if (filterDigits.length >= 3 && plan.patientPhone) {
            const phoneDigits = String(plan.patientPhone).replace(/\D/g, '');
            if (phoneDigits.includes(filterDigits)) return true;
        }
        return false;
    };
    // Newest first, but cancelled plans sink to the bottom — they're
    // terminal and otherwise crowd out the plans staff still act on (e.g.
    // a cancelled plan immediately re-created as a replacement).
    const visiblePlans = plans
        .filter(matchesFilter)
        .sort((a, b) =>
            (a.status === 'cancelled') - (b.status === 'cancelled')
            || String(b.createdAt || '').localeCompare(String(a.createdAt || '')));

    return (
        // textAlign: 'left' on the outer wrapper neutralises the CRA
        // template's ``.App { text-align: center }`` for the whole tab.
        // Empty-state / loading divs that *want* centered text still set
        // it explicitly, so this is purely additive.
        <div style={{ textAlign: 'left' }}>
            {/* --- Header --- */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 16,
                marginBottom: 20,
                flexWrap: 'wrap',
            }}>
                <div>
                    <h2 style={{ margin: '0 0 4px 0', color: '#1a1d1b', fontSize: '1.5rem' }}>
                        Payment plans
                    </h2>
                    <p style={{ margin: 0, color: '#6b716d', fontSize: '0.95rem' }}>
                        Monthly auto-charge schedules for enrolled patients.
                    </p>
                </div>
                <button
                    onClick={() => setShowEnroll(true)}
                    style={{
                        padding: '10px 18px',
                        backgroundColor: '#1f5a3d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                    }}
                >
                    + New payment plan
                </button>
            </div>

            {/* --- Toolbar: status filter + patient search (same controls as
                the Invoices tab). Status is a server-side filter; the search
                narrows the loaded list client-side and survives status
                switches. --- */}
            <div className="pm-toolbar" style={{ padding: 0, marginBottom: 12 }}>
                <div className="pm-segmented" role="group" aria-label="Payment plan status">
                    {[
                        { id: 'all', label: 'All' },
                        { id: 'active', label: 'Active' },
                        { id: 'defaulted', label: 'Needs attention', count: defaultedCount, danger: true },
                        { id: 'paused', label: 'Paused' },
                        { id: 'completed', label: 'Completed' },
                        { id: 'cancelled', label: 'Cancelled' },
                    ].map(p => (
                        <button
                            key={p.id}
                            type="button"
                            className="pm-segment"
                            aria-pressed={statusFilter === p.id}
                            onClick={() => setStatusFilter(p.id)}
                        >
                            {p.label}
                            {p.count > 0 && (
                                <span className={`pm-segment-count${p.danger ? ' pm-segment-count-danger' : ''}`}>
                                    {p.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
                <div className="pm-search pm-search-compact pm-toolbar-end" style={{ flex: '0 1 320px', minWidth: 220 }}>
                    <Icon name="search" size={15} />
                    <input
                        type="text"
                        value={planFilter}
                        onChange={(e) => setPlanFilter(e.target.value)}
                        placeholder="Filter by patient name, email, or phone"
                        aria-label="Filter payment plans by patient"
                    />
                    {planFilter && (
                        <button
                            type="button"
                            className="pm-search-clear"
                            onClick={() => setPlanFilter('')}
                            aria-label="Clear filter"
                            title="Clear filter"
                        >
                            ✕
                        </button>
                    )}
                </div>
            </div>
            {planFilter && !loading && (
                <div className="pm-toolbar" style={{ padding: 0, marginBottom: 12, justifyContent: 'flex-end' }}>
                    <span className="pm-filter-count">
                        Showing {visiblePlans.length} of {plans.length} plans
                    </span>
                    <button type="button" className="pm-btn pm-btn-sm" onClick={() => setPlanFilter('')}>
                        Clear filters
                    </button>
                </div>
            )}

            {/* --- Defaulted banner --- */}
            {defaultedCount > 0 && statusFilter !== 'defaulted' && (
                <div style={{
                    padding: '12px 16px',
                    marginBottom: 20,
                    background: '#fef5f4',
                    border: '1px solid #f5c6c1',
                    borderRadius: 8,
                    color: '#912018',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                    <span>
                        ⚠️ {defaultedCount} plan{defaultedCount === 1 ? '' : 's'} need attention
                        — recurring charges have been paused after consecutive failures.
                    </span>
                    <button
                        onClick={() => setStatusFilter('defaulted')}
                        style={{
                            padding: '6px 12px',
                            background: 'white',
                            color: '#912018',
                            border: '1px solid #f5c6c1',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontWeight: 500,
                        }}
                    >
                        Review →
                    </button>
                </div>
            )}

            {/* --- Toast --- */}
            {toast && (
                <div style={{
                    position: 'fixed', top: 24, right: 24, zIndex: 1000,
                    padding: '12px 20px',
                    background: toast.type === 'error' ? '#fdecea' : '#e7f4ec',
                    color: toast.type === 'error' ? '#912018' : '#1d6b40',
                    border: '1px solid',
                    borderColor: toast.type === 'error' ? '#f5c6c1' : '#c6e3d0',
                    borderRadius: 8,
                    fontWeight: 500,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}>{toast.message}</div>
            )}

            {/* --- Table --- */}
            <PlansTable
                plans={visiblePlans}
                totalCount={plans.length}
                filterText={planFilter}
                onClearFilter={() => setPlanFilter('')}
                loading={loading}
                errorMsg={errorMsg}
                onOpen={(p) => openDetail(p.id)}
            />

            {showDetail && selectedPlan && (
                <PlanDetailDrawer
                    practiceId={practiceId}
                    plan={selectedPlan.plan}
                    invoices={selectedPlan.invoices}
                    onClose={() => { setShowDetail(false); setSelectedPlan(null); }}
                    onChanged={async () => { await refreshDetail(); await loadPlans(); }}
                    showToast={showToast}
                />
            )}

            {showEnroll && (
                <EnrollPlanModal
                    practiceId={practiceId}
                    showToast={showToast}
                    onClose={() => setShowEnroll(false)}
                    onCreated={async ({ firstCharge, firstInvoice }) => {
                        setShowEnroll(false);
                        await loadPlans();
                        // Three flavours of feedback, mirroring the
                        // backend branches:
                        //   1. firstCharge.success — plan was for
                        //      today (or staff forced charge), invoice
                        //      minted + charged inline.
                        //   2. firstCharge present but failed/skipped
                        //      — plan was today's, charge attempted
                        //      and failed; invoice still exists for
                        //      retry.
                        //   3. firstInvoice present — plan is future-
                        //      dated, invoice provisioned now,
                        //      charge waits for the due date.
                        if (firstCharge && firstCharge.success) {
                            showToast('Plan created and first installment charged.');
                        } else if (firstCharge && !firstCharge.skipped && firstCharge.success === false) {
                            showToast(
                                `Plan created but first charge failed: ${firstCharge.error || firstCharge.code || 'unknown error'}`,
                                'error',
                            );
                        } else if (firstInvoice && firstInvoice.success) {
                            showToast(
                                'Plan created — first invoice is ready. Cron will charge on the due date, or staff/patient can pay it sooner.',
                            );
                        } else if (firstInvoice && firstInvoice.success === false) {
                            showToast(
                                `Plan created but first invoice could not be provisioned: ${firstInvoice.error || firstInvoice.code || 'unknown error'}. Cron will retry on the due date.`,
                                'error',
                            );
                        } else {
                            showToast('Payment plan created.');
                        }
                    }}
                />
            )}
        </div>
    );
};

// ============================================================================
// Table
// ============================================================================

const PlansTable = ({ plans, totalCount, filterText, onClearFilter, loading, errorMsg, onOpen }) => {
    if (loading) {
        return <div style={{ padding: 40, textAlign: 'center', color: '#6b716d' }}>Loading plans…</div>;
    }
    if (errorMsg) {
        return (
            <div style={{
                padding: 16, background: '#fdecea', border: '1px solid #f5c6c1',
                borderRadius: 8, color: '#912018',
            }}>
                {errorMsg}
            </div>
        );
    }
    // Plans exist but the patient search hides them all — say so, rather
    // than the "No payment plans yet" onboarding copy below.
    if (!plans.length && totalCount > 0 && filterText) {
        return (
            <div style={{
                padding: 40, textAlign: 'center', background: 'white',
                border: '1px solid #e3e7e4', borderRadius: 8,
            }}>
                <p style={{ margin: 0, color: '#6b716d' }}>
                    No payment plans match "{filterText}".
                </p>
                <button
                    type="button"
                    onClick={onClearFilter}
                    style={{
                        marginTop: '10px',
                        padding: '6px 14px',
                        fontSize: '12px',
                        border: '1px solid #d6dbd7',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        color: '#3c4540',
                        cursor: 'pointer'
                    }}
                >
                    Clear filter
                </button>
            </div>
        );
    }
    if (!plans.length) {
        return (
            <div style={{
                padding: 60, textAlign: 'center', background: 'white',
                border: '1px solid #e3e7e4', borderRadius: 8,
            }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📅</div>
                <h3 style={{ margin: '0 0 8px 0', color: '#1a1d1b' }}>No payment plans yet</h3>
                <p style={{ margin: 0, color: '#6b716d' }}>
                    Click <strong>+ New payment plan</strong> to enroll a patient in a monthly schedule.
                </p>
            </div>
        );
    }

    return (
        <div style={{
            background: 'white', border: '1px solid #e3e7e4', borderRadius: 8,
            overflow: 'hidden',
        }}>
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 1.6fr 1fr 1fr 1fr 0.8fr',
                gap: 0,
                padding: '12px 16px',
                background: '#f7f8f7',
                borderBottom: '1px solid #e3e7e4',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#6b716d',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
            }}>
                <div>Patient</div>
                <div>Plan</div>
                <div>Progress</div>
                <div>Next charge</div>
                <div>Total / installment</div>
                <div>Status</div>
            </div>
            {plans.map(p => (
                <PlanRow key={p.id} plan={p} onOpen={() => onOpen(p)} />
            ))}
        </div>
    );
};

const PlanRow = ({ plan, onOpen }) => {
    const completed = plan.installmentsCompleted || 0;
    const total = plan.installments || 0;
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    const ss = statusStyle(plan.status);
    return (
        <div
            onClick={onOpen}
            style={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 1.6fr 1fr 1fr 1fr 0.8fr',
                gap: 0,
                padding: '14px 16px',
                borderBottom: '1px solid #eef0ee',
                cursor: 'pointer',
                alignItems: 'center',
                background: plan.status === 'defaulted' ? '#fef5f4' : 'white',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = plan.status === 'defaulted' ? '#fef5f4' : '#f7f8f7'}
            onMouseLeave={(e) => e.currentTarget.style.background = plan.status === 'defaulted' ? '#fef5f4' : 'white'}
        >
            <div>
                <div data-phi style={{ fontWeight: 500, color: '#1a1d1b' }}>
                    {plan.patientName || plan.patientId}
                </div>
                <div data-phi style={{ fontSize: '0.8rem', color: '#8a908c' }}>ID: {plan.patientId}</div>
            </div>
            <div>
                <div style={{ fontWeight: 500, color: '#1a1d1b' }}>
                    {plan.name || 'Payment plan'}
                </div>
                {plan.description && (
                    <div style={{ fontSize: '0.8rem', color: '#8a908c',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        maxWidth: 280,
                    }}>
                        {plan.description}
                    </div>
                )}
            </div>
            <div>
                <div style={{ fontSize: '0.9rem', color: '#1a1d1b' }}>
                    {completed} / {total}
                </div>
                <div style={{
                    height: 4, background: '#e3e7e4', borderRadius: 2, marginTop: 4,
                    overflow: 'hidden', maxWidth: 100,
                }}>
                    <div style={{
                        width: `${pct}%`, height: '100%',
                        background: plan.status === 'defaulted' ? '#b42318'
                                  : plan.status === 'completed' ? '#1d6b40'
                                  : '#1f5a3d',
                    }}/>
                </div>
            </div>
            <div style={{ fontSize: '0.9rem', color: '#1a1d1b' }}>
                {plan.nextChargeDate ? formatDate(plan.nextChargeDate) : '—'}
                {plan.nextChargeDate && plan.status === 'active' && (() => {
                    const rel = formatRelativeDate(plan.nextChargeDate);
                    if (!rel) return null;
                    // Amber when the cron should already have run
                    // (date is today or earlier) — useful signal that
                    // a plan is mid-charge or stuck. Muted gray for
                    // future dates so the column stays scannable.
                    const overdueOrToday = rel === 'today'
                        || rel === 'yesterday'
                        || rel.endsWith('days ago');
                    return (
                        <div style={{
                            color: overdueOrToday ? '#a8520c' : '#8a908c',
                            fontSize: '0.8rem',
                            marginTop: 1,
                        }}>
                            ({rel})
                        </div>
                    );
                })()}
            </div>
            <div style={{ fontSize: '0.9rem' }}>
                <div style={{ color: '#1a1d1b', fontWeight: 500 }}>
                    {formatCurrencyCents(plan.totalAmountCents)}
                </div>
                <div style={{ color: '#8a908c', fontSize: '0.8rem' }}>
                    {formatCurrencyCents(plan.installmentAmountCents)} / mo
                </div>
            </div>
            <div>
                <span style={{
                    padding: '4px 10px',
                    borderRadius: 12,
                    background: ss.bg,
                    color: ss.color,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                }}>{ss.label}</span>
            </div>
        </div>
    );
};

// ============================================================================
// Detail drawer
// ============================================================================

const PlanDetailDrawer = ({ practiceId, plan, invoices, onClose, onChanged, showToast }) => {
    const [busyAction, setBusyAction] = useState(null);
    const [showCancel, setShowCancel] = useState(false);
    const [showUpdateCard, setShowUpdateCard] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const ss = statusStyle(plan.status);

    const isTerminal = plan.status === 'completed' || plan.status === 'cancelled';

    const wrap = async (label, fn) => {
        if (busyAction) return;
        setBusyAction(label);
        try {
            const res = await fn();
            if (res?.ok && res?.data?.success !== false) {
                showToast(`${label} succeeded`);
                await onChanged();
            } else {
                const err = res?.data?.error || res?.data?.code || 'Unknown error';
                showToast(`${label} failed: ${err}`, 'error');
            }
        } catch (e) {
            showToast(`${label} failed: ${e?.message || e}`, 'error');
        } finally {
            setBusyAction(null);
        }
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 900,
                display: 'flex', justifyContent: 'flex-end',
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: 'min(720px, 100%)',
                    height: '100vh',
                    background: '#f7f8f7',
                    overflow: 'auto',
                    boxShadow: '-4px 0 16px rgba(0,0,0,0.08)',
                    // Same reason as ModalShell — ``.App`` sets
                    // ``text-align: center`` globally; force left
                    // alignment for the whole detail drawer so headings,
                    // activity rows, and stats line up the InvoiceTab way.
                    textAlign: 'left',
                }}
            >
                {/* Header */}
                <div style={{
                    padding: '20px 24px',
                    background: 'white',
                    borderBottom: '1px solid #e3e7e4',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <h2 style={{ margin: 0, color: '#1a1d1b', fontSize: '1.3rem' }}>
                                    {plan.name || 'Payment plan'}
                                </h2>
                                <span style={{
                                    padding: '4px 10px',
                                    borderRadius: 12,
                                    background: ss.bg,
                                    color: ss.color,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                }}>{ss.label}</span>
                            </div>
                            <div data-phi style={{ marginTop: 4, color: '#6b716d', fontSize: '0.9rem' }}>
                                {plan.patientName || plan.patientId} · ID: {plan.patientId}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            aria-label="Close"
                            style={{
                                background: 'transparent', border: 'none', cursor: 'pointer',
                                fontSize: '1.5rem', color: '#8a908c', lineHeight: 1,
                            }}
                        >×</button>
                    </div>

                    {plan.description && (
                        <p style={{ margin: '12px 0 0 0', color: '#3c4540', fontSize: '0.9rem' }}>
                            {plan.description}
                        </p>
                    )}
                </div>

                {/* Defaulted alert */}
                {plan.status === 'defaulted' && (
                    <div style={{
                        margin: '16px 24px 0 24px',
                        padding: '14px 16px',
                        background: '#fef5f4',
                        border: '1px solid #f5c6c1',
                        borderRadius: 8,
                        color: '#912018',
                    }}>
                        <strong>⚠️ Plan defaulted</strong> — {plan.consecutiveFailures} consecutive
                        charges failed. Auto-charging is paused. Update the card on file or charge
                        manually after resolving with the patient.
                    </div>
                )}

                {/* Action bar */}
                <div style={{ padding: '16px 24px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {plan.status === 'active' && (
                        <>
                            <ActionButton
                                label="Charge now"
                                primary
                                disabled={!!busyAction}
                                busy={busyAction === 'Charge'}
                                onClick={() => wrap('Charge', () =>
                                    chargePaymentPlanNow(practiceId, plan.id))}
                            />
                            <ActionButton
                                label="Pause"
                                disabled={!!busyAction}
                                busy={busyAction === 'Pause'}
                                onClick={() => wrap('Pause', () =>
                                    pausePaymentPlan(practiceId, plan.id))}
                            />
                        </>
                    )}
                    {plan.status === 'paused' && (
                        <ActionButton
                            label="Resume"
                            primary
                            disabled={!!busyAction}
                            busy={busyAction === 'Resume'}
                            onClick={() => wrap('Resume', () =>
                                resumePaymentPlan(practiceId, plan.id))}
                        />
                    )}
                    {!isTerminal && (
                        <>
                            <ActionButton
                                label="Update card"
                                disabled={!!busyAction}
                                onClick={() => setShowUpdateCard(true)}
                            />
                            <ActionButton
                                label="Cancel plan"
                                danger
                                disabled={!!busyAction}
                                onClick={() => setShowCancel(true)}
                            />
                        </>
                    )}
                </div>

                {/* Stats grid */}
                <div style={{ padding: '0 24px' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                        gap: 12,
                    }}>
                        <Stat label="Progress" value={
                            `${plan.installmentsCompleted || 0} of ${plan.installments || 0}`
                        }/>
                        <Stat label="Total" value={formatCurrencyCents(plan.totalAmountCents)}/>
                        <Stat label="Installment" value={formatCurrencyCents(plan.installmentAmountCents) + ' / mo'}/>
                        {/*
                            "Charged so far" tracks plan *principal* collected
                            (installmentsCompleted × installmentAmountCents),
                            not gross card volume. The plan represents a debt
                            of ``totalAmountCents`` toward the practice; any
                            surcharge collected on top is a processor fee
                            paid by the patient and isn't part of plan
                            progress. The raw doc still keeps
                            ``totalChargedCents`` as the gross figure for
                            reconciliation purposes — the UI just chooses to
                            present principal here so "Charged so far"
                            matches "Total" arithmetically (e.g. 12/12 of
                            $200 = $2,400 = the plan total) regardless of
                            whether surcharging is enabled.
                        */}
                        <Stat
                            label="Charged so far"
                            value={formatCurrencyCents(
                                (Number(plan.installmentsCompleted) || 0) *
                                (Number(plan.installmentAmountCents) || 0),
                            )}
                        />
                        <Stat label="Started" value={formatDate(plan.startDate)}/>
                        <Stat label={plan.status === 'completed' ? 'Completed' : 'Next charge'}
                              value={plan.nextChargeDate ? formatDate(plan.nextChargeDate)
                                                        : (plan.completedAt ? formatDate(plan.completedAt) : '—')}/>
                    </div>
                </div>

                {/* Installment invoices */}
                <Section title={`Installment invoices (${invoices.length})`}>
                    {!invoices.length && (
                        <div style={{ padding: 16, color: '#8a908c', fontStyle: 'italic' }}>
                            No installment invoices have been generated yet. The first invoice will
                            be created on {formatDate(plan.nextChargeDate || plan.startDate)}.
                        </div>
                    )}
                    {!!invoices.length && (
                        <div style={{ background: 'white', border: '1px solid #e3e7e4', borderRadius: 8 }}>
                            <InvoiceMiniHeader/>
                            {invoices
                                .sort((a, b) => (a.paymentPlanInstallmentNumber || 0) - (b.paymentPlanInstallmentNumber || 0))
                                .map(inv => <InvoiceMiniRow key={inv.id} invoice={inv}/>)}
                        </div>
                    )}
                </Section>

                {/* Activity log */}
                <Section title="Activity">
                    {!plan.activityLog?.length && (
                        <div style={{ padding: 16, color: '#8a908c', fontStyle: 'italic' }}>No activity yet.</div>
                    )}
                    {!!plan.activityLog?.length && (
                        <div style={{ background: 'white', border: '1px solid #e3e7e4', borderRadius: 8 }}>
                            {[...plan.activityLog].reverse().map((ev, i) => (
                                <ActivityRow key={i} entry={ev}/>
                            ))}
                        </div>
                    )}
                </Section>

                {/* Modals */}
                {showCancel && (
                    <ConfirmModal
                        title="Cancel this payment plan?"
                        body={
                            <>
                                <p>Cancelling stops all future charges. Past installments stay paid.</p>
                                <textarea
                                    value={cancelReason}
                                    onChange={(e) => setCancelReason(e.target.value)}
                                    placeholder="Reason (optional)"
                                    rows={3}
                                    style={{
                                        width: '100%', padding: 8, marginTop: 8,
                                        border: '1px solid #d6dbd7', borderRadius: 6,
                                        fontFamily: 'inherit',
                                    }}
                                />
                            </>
                        }
                        confirmLabel="Cancel plan"
                        confirmDanger
                        onConfirm={async () => {
                            setShowCancel(false);
                            await wrap('Cancel', () => cancelPaymentPlan(practiceId, plan.id, cancelReason));
                        }}
                        onClose={() => setShowCancel(false)}
                    />
                )}
                {showUpdateCard && (
                    <UpdateCardModal
                        practiceId={practiceId}
                        plan={plan}
                        onClose={() => setShowUpdateCard(false)}
                        onUpdated={async () => {
                            setShowUpdateCard(false);
                            await onChanged();
                            showToast('Card updated');
                        }}
                        showToast={showToast}
                    />
                )}
            </div>
        </div>
    );
};

// ============================================================================
// Detail subcomponents
// ============================================================================

const Stat = ({ label, value }) => (
    <div style={{
        background: 'white', padding: 12, borderRadius: 8,
        border: '1px solid #e3e7e4',
    }}>
        <div style={{ fontSize: '0.75rem', color: '#8a908c', textTransform: 'uppercase',
                      letterSpacing: '0.04em', fontWeight: 600 }}>
            {label}
        </div>
        <div style={{ marginTop: 4, color: '#1a1d1b', fontSize: '0.95rem', fontWeight: 500 }}>
            {value}
        </div>
    </div>
);

const Section = ({ title, children }) => (
    <div style={{ padding: '24px 24px 8px 24px' }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6b716d',
                     textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {title}
        </h3>
        {children}
    </div>
);

const ActionButton = ({ label, onClick, primary, danger, disabled, busy }) => {
    const base = {
        padding: '8px 16px', borderRadius: 6, cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: '0.9rem', fontWeight: 500, border: '1px solid transparent',
        opacity: disabled ? 0.6 : 1,
    };
    const themed = primary
        ? { ...base, background: '#1f5a3d', color: 'white' }
        : danger
            ? { ...base, background: 'white', color: '#b42318', borderColor: '#f5c6c1' }
            : { ...base, background: 'white', color: '#3c4540', borderColor: '#d6dbd7' };
    return (
        <button onClick={onClick} disabled={disabled} style={themed}>
            {busy ? '…' : label}
        </button>
    );
};

// Shared grid template — header + rows must agree, otherwise the
// columns drift. Five date/text/amount/status columns; the second
// (invoice id) flexes to fill remaining space.
const INVOICE_MINI_COLUMNS = '50px 1fr 110px 110px 110px 80px';

const InvoiceMiniHeader = () => (
    <div style={{
        display: 'grid',
        gridTemplateColumns: INVOICE_MINI_COLUMNS,
        gap: 12,
        padding: '8px 14px',
        borderBottom: '1px solid #e3e7e4',
        background: '#f7f8f7',
        fontSize: '0.7rem',
        color: '#6b716d',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        fontWeight: 600,
    }}>
        <div>#</div>
        <div>Invoice</div>
        <div>Due</div>
        <div>Paid</div>
        <div>Amount</div>
        <div>Status</div>
    </div>
);

const InvoiceMiniRow = ({ invoice }) => {
    const statusColors = {
        paid: { bg: '#e7f4ec', color: '#1d6b40' },
        processing: { bg: '#e8f0f7', color: '#1f4e79' },
        pending: { bg: '#fdf3e3', color: '#a8520c' },
        failed: { bg: '#fdecea', color: '#912018' },
    };
    const sc = statusColors[(invoice.status || '').toLowerCase()] || { bg: '#eef0ee', color: '#3c4540' };
    // ``paidAt`` is stamped by the payin.succeeded webhook handler
    // (server.py ``invoice_doc['paidAt'] = now_iso()``). We render it
    // even when it differs from ``dueDate`` so staff can see at a
    // glance "patient paid early today vs charge was scheduled for
    // tomorrow" — exactly the case where an early public-link
    // payment landed before the cron's run.
    const paidAt = invoice.paidAt;
    const paidEarly = !!(
        paidAt
        && invoice.dueDate
        && (paidAt.slice(0, 10) < invoice.dueDate)
    );
    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: INVOICE_MINI_COLUMNS,
            gap: 12,
            padding: '10px 14px',
            borderBottom: '1px solid #eef0ee',
            alignItems: 'center',
            fontSize: '0.9rem',
        }}>
            <div style={{ color: '#6b716d', fontWeight: 600 }}>
                #{invoice.paymentPlanInstallmentNumber || '?'}
            </div>
            <div style={{ color: '#1a1d1b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {invoice.id}
            </div>
            <div style={{ color: '#6b716d' }}>
                {formatDate(invoice.dueDate)}
            </div>
            <div style={{
                color: paidAt ? '#1d6b40' : '#8a908c',
                fontWeight: paidAt ? 500 : 400,
            }}>
                {paidAt ? formatDate(paidAt) : '—'}
                {paidEarly && (
                    <span style={{
                        marginLeft: 6, fontSize: '0.7rem',
                        color: '#1d6b40',
                        background: '#e7f4ec',
                        padding: '1px 6px', borderRadius: 8,
                        fontWeight: 600,
                    }}>early</span>
                )}
            </div>
            <div style={{ color: '#1a1d1b', fontWeight: 500 }}>
                ${(invoice.total || 0).toFixed(2)}
            </div>
            <div>
                <span style={{
                    padding: '2px 8px', borderRadius: 10,
                    background: sc.bg, color: sc.color,
                    fontSize: '0.7rem', fontWeight: 600, textTransform: 'capitalize',
                }}>{invoice.status}</span>
            </div>
        </div>
    );
};

const ActivityRow = ({ entry }) => {
    const labels = {
        enrolled: { icon: '✨', label: 'Plan created' },
        installment_invoice_provisioned: { icon: '🧾', label: 'Next invoice scheduled' },
        installment_charge_attempted: { icon: '⏳', label: 'Charge attempted' },
        installment_charge_succeeded: { icon: '✅', label: 'Payment received' },
        installment_charge_failed: { icon: '❌', label: 'Charge failed' },
        paused: { icon: '⏸️', label: 'Paused' },
        resumed: { icon: '▶️', label: 'Resumed' },
        cancelled: { icon: '🚫', label: 'Cancelled' },
        completed: { icon: '🏁', label: 'Plan completed' },
        defaulted: { icon: '⚠️', label: 'Plan defaulted' },
        card_changed: { icon: '💳', label: 'Card updated' },
    };
    const humanize = (s) => String(s || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    const info = labels[entry.action] || { icon: '•', label: humanize(entry.action) };
    return (
        <div style={{
            padding: '10px 14px',
            borderBottom: '1px solid #eef0ee',
            display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
            <div style={{ fontSize: '1.1rem', lineHeight: 1.2 }}>{info.icon}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ color: '#1a1d1b', fontWeight: 500 }}>{info.label}</span>
                    <span style={{ color: '#8a908c', fontSize: '0.8rem' }}>
                        {formatDateTime(entry.timestamp)}
                    </span>
                </div>
                {entry.details && Object.keys(entry.details).length > 0 && (
                    <div style={{
                        marginTop: 4, fontSize: '0.8rem', color: '#6b716d',
                        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                    }}>
                        {Object.entries(entry.details)
                            .filter(([_, v]) => v !== null && v !== undefined && v !== '')
                            .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
                            .join(' · ')}
                    </div>
                )}
            </div>
        </div>
    );
};

// ============================================================================
// Update-card modal — picks a different active card on the same patient
// ============================================================================

const UpdateCardModal = ({ practiceId, plan, onClose, onUpdated, showToast }) => {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState('');
    const [busy, setBusy] = useState(false);
    // Loaded once for the per-row surcharge preview line. We mirror the
    // enrollment modal's behaviour so the swap-card flow shows the same
    // "+ 3% surcharge ($6.00)" hint per credit card the staffer is
    // about to lock the plan onto.
    const [practiceCfg, setPracticeCfg] = useState(null);

    useEffect(() => {
        loadPracticePaymentsConfig(practiceId).then(setPracticeCfg);
    }, [practiceId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const resp = await listPatientPaymentMethods(practiceId, plan.patientId);
                const list = (resp?.paymentMethods || resp?.data || []).filter(c => c.status === 'ACTIVE');
                if (!cancelled) {
                    setCards(list);
                    // Pre-select the current card or the default one.
                    setSelectedId(plan.paymentMethodVaultId
                        || list.find(c => c.isDefault)?.id
                        || list[0]?.id
                        || '');
                }
            } catch (e) {
                console.error(e);
                if (!cancelled) showToast('Failed to load cards', 'error');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [practiceId, plan.patientId, plan.paymentMethodVaultId, showToast]);

    const submit = async () => {
        if (!selectedId) return;
        setBusy(true);
        try {
            const res = await updatePaymentPlanCard(practiceId, plan.id, selectedId);
            if (res?.ok && res?.data?.success !== false) {
                onUpdated();
            } else {
                showToast(`Update failed: ${res?.data?.error || 'unknown error'}`, 'error');
                setBusy(false);
            }
        } catch (e) {
            showToast(`Update failed: ${e?.message || e}`, 'error');
            setBusy(false);
        }
    };

    return (
        <ModalShell title="Update card on file" onClose={onClose}>
            {loading ? (
                <div style={{ padding: 16 }}>Loading cards…</div>
            ) : !cards.length ? (
                <div style={{ padding: 16, color: '#6b716d' }}>
                    This patient has no active saved cards. Add a card from the patient's profile first.
                </div>
            ) : (
                <>
                    <p style={{ margin: '0 0 12px 0', color: '#3c4540', fontSize: '0.9rem' }}>
                        Choose which saved card the plan should draw from. This will reset the
                        failure counter and resume the plan if it's paused or defaulted.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {cards.map(c => (
                            <label key={c.id} style={{
                                display: 'flex', gap: 12, alignItems: 'center', padding: '10px 12px',
                                border: '1px solid', borderColor: selectedId === c.id ? '#1f5a3d' : '#e3e7e4',
                                borderRadius: 8, cursor: 'pointer',
                                background: selectedId === c.id ? '#f3f7fb' : 'white',
                            }}>
                                <input
                                    type="radio"
                                    checked={selectedId === c.id}
                                    onChange={() => setSelectedId(c.id)}
                                />
                                <div style={{ flex: 1 }}>
                                    <CardLabel
                                        card={c}
                                        extraBadges={[
                                            c.id === plan.paymentMethodVaultId && {
                                                label: 'Current',
                                                bg: '#fdf3e3', color: '#a8520c', border: '#f3d9ae',
                                            },
                                        ]}
                                        baseAmountCents={plan.installmentAmountCents}
                                        practiceCfg={practiceCfg}
                                    />
                                </div>
                            </label>
                        ))}
                    </div>
                    <ModalFooter>
                        <ActionButton label="Cancel" onClick={onClose} disabled={busy}/>
                        <ActionButton label="Update card" primary onClick={submit}
                                      disabled={busy || !selectedId} busy={busy}/>
                    </ModalFooter>
                </>
            )}
        </ModalShell>
    );
};

// Render a saved-card label uniformly. The staff/public endpoints both
// nest card details under ``card.{brand,last4,expirationMonth,...}``;
// pulling that into one component keeps the two callsites in sync if
// the shape ever changes.
//
// Format intentionally mirrors the "Charge Card on File" modal in
// InvoiceTab so staff get a consistent card-picker experience across
// the two flows. When ``baseAmountCents`` and ``practiceCfg`` are
// provided and the card is a surchargeable credit card, an amber
// "+ X% surcharge ($Y.YY)" line is rendered below the exp date — same
// visual as InvoiceTab's chargeCardMethods.map row.
const CardLabel = ({ card, extraBadges = [], baseAmountCents, practiceCfg }) => {
    const c = card.card || {};
    const brand = (c.brandDesc || c.brand || 'Card').replace(/_/g, ' ');
    const exp = (c.expirationMonth && c.expirationYear)
        ? `Exp ${String(c.expirationMonth).padStart(2, '0')}/${String(c.expirationYear).slice(-2)}`
        : '';
    const cardType = (c.type || '').toUpperCase();
    const typeLabel = cardType === 'CREDIT'
        ? 'Credit'
        : cardType === 'DEBIT'
            ? 'Debit'
            : null;

    // Compute a row-level surcharge preview when caller passes the
    // base amount + practice config. Skipped (returns null) for debit
    // cards, no-state practices, surcharge-off practices, etc.
    const preview = (baseAmountCents != null && practiceCfg)
        ? computeSurchargePreview(baseAmountCents, card, practiceCfg)
        : null;
    const fmtCents = (cents) => {
        if (typeof cents !== 'number' || !Number.isFinite(cents)) return null;
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
            .format(cents / 100);
    };

    return (
        // textAlign: 'left' is load-bearing — App.css ships ``.App {
        // text-align: center }`` from the CRA template, which leaks into
        // every label inside our modals. The InvoiceTab "Charge Card on
        // File" rows force-left-align the same way; matching that keeps
        // the two card pickers visually consistent.
        <div style={{ textAlign: 'left', minWidth: 0 }}>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                color: '#1a1d1b', fontWeight: 500,
            }}>
                <span>
                    {brand} •••• {c.last4 || '????'}{card.isDefault ? ' · Default' : ''}
                </span>
                {typeLabel && (
                    <span style={{
                        fontSize: 11,
                        fontWeight: 500,
                        padding: '1px 7px',
                        borderRadius: 999,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        backgroundColor: typeLabel === 'Credit' ? '#fdf3e3' : '#e6f6f3',
                        color: typeLabel === 'Credit' ? '#a8520c' : '#0e7c66',
                        border: `1px solid ${typeLabel === 'Credit' ? '#f3d9ae' : '#b6e5dc'}`,
                    }}>
                        {typeLabel}
                    </span>
                )}
                {extraBadges.filter(Boolean).map((b, i) => (
                    <span key={i} style={{
                        fontSize: 11,
                        fontWeight: 500,
                        padding: '1px 7px',
                        borderRadius: 999,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        backgroundColor: b.bg,
                        color: b.color,
                        border: `1px solid ${b.border || b.bg}`,
                    }}>{b.label}</span>
                ))}
            </div>
            {exp && (
                <div style={{ color: '#8a908c', fontSize: 12, marginTop: 2 }}>
                    {exp}
                </div>
            )}
            {/* Per-installment cost line. For surchargeable credit
                cards we lead with the surcharge breakdown (matching
                the amber styling InvoiceTab uses for its
                Charge-Card-on-File rows) and append the surcharged
                total. For everything else (debit, non-surchargeable
                credit, surcharge-off practices) we just show the
                base total per month — staff still want to see "Total
                $200.00 / mo" next to each card so they can confirm
                the amount before committing the patient. */}
            {Number.isFinite(baseAmountCents) && baseAmountCents > 0 && (
                preview ? (
                    <div style={{
                        color: '#a8520c',
                        fontSize: 12,
                        marginTop: 4,
                        fontWeight: 500,
                    }}>
                        + {preview.pct}% surcharge ({fmtCents(preview.surchargeCents)})
                        {' · '}
                        <span style={{ color: '#1a1d1b' }}>
                            Total {fmtCents(preview.totalCents)} / mo
                        </span>
                    </div>
                ) : (
                    <div style={{
                        color: '#1a1d1b',
                        fontSize: 12,
                        marginTop: 4,
                        fontWeight: 500,
                    }}>
                        Total {fmtCents(baseAmountCents)} / mo
                    </div>
                )
            )}
        </div>
    );
};

// ============================================================================
// Enrollment modal
// ============================================================================

const EnrollPlanModal = ({ practiceId, onClose, onCreated, showToast }) => {
    // Patient search + selection -------------------------------------------
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [patient, setPatient] = useState(null);

    // Plan inputs -----------------------------------------------------------
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [totalDollars, setTotalDollars] = useState('');
    const [installments, setInstallments] = useState('12');
    // Default to tomorrow (in the staff member's *local* tz). Today
    // would auto-charge the first installment the moment the cron
    // fires after enrollment, which is rarely what the staffer wants —
    // they typically want a beat to confirm the card is right and
    // notify the patient before money moves. Tomorrow gives them that
    // buffer while still keeping the plan tightly scheduled. Staff
    // can override to today (or any future date) right in the picker.
    //
    // Compose YYYY-MM-DD from local date components rather than
    // ``new Date().toISOString().split('T')[0]`` — toISOString()
    // converts to UTC first, so anywhere west of UTC after ~5pm local
    // would default to "tomorrow UTC" and the date input renders that
    // verbatim, surprising the user with a +1 day offset.
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    });

    // Card selection -------------------------------------------------------
    const [cards, setCards] = useState([]);
    const [cardsLoading, setCardsLoading] = useState(false);
    const [selectedCardId, setSelectedCardId] = useState('');
    // When true, swap in the inline manual card-entry popup so staff can
    // vault a card for this patient without leaving the enrollment flow.
    const [showAddCardForm, setShowAddCardForm] = useState(false);

    // Practice payments cfg (for surcharge note) ---------------------------
    const [practiceCfg, setPracticeCfg] = useState({});

    // Existing-plans guardrail -------------------------------------------
    // Multi-plan enrollment is supported by the backend (no uniqueness
    // constraint on patientId), but it's almost always a staff mistake —
    // they search the patient, forget the plan from last month, and
    // accidentally double-enroll. We surface any "live" plans (active /
    // paused / defaulted) the moment the patient is picked and gate
    // submission behind an explicit confirm so it stays a deliberate
    // choice rather than a slip.
    const [existingPlans, setExistingPlans] = useState([]);
    const [existingPlansLoading, setExistingPlansLoading] = useState(false);
    const [confirmDuplicate, setConfirmDuplicate] = useState(false);
    const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        loadPracticePaymentsConfig(practiceId).then(setPracticeCfg);
    }, [practiceId]);

    // Debounced patient search.
    useEffect(() => {
        if (!searchTerm.trim()) { setSearchResults([]); return; }
        const handle = setTimeout(async () => {
            setSearching(true);
            try {
                const data = await fetchFromPaymentsAPI(
                    `/api/payments/patients/search?practiceId=${practiceId}&q=${encodeURIComponent(searchTerm)}`,
                );
                setSearchResults(data?.success ? (data.patients || []) : []);
            } catch (e) {
                console.error(e);
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        }, 250);
        return () => clearTimeout(handle);
    }, [searchTerm, practiceId]);

    // Load cards once a patient is picked. The patient-search endpoint
    // returns ``patientId`` (the practice's canonical patient identifier;
    // see search_patients in server.py); ``id`` is the Cosmos doc id and
    // is not the right partition for the saved-cards lookup.
    const selectedPatientId = patient?.patientId || patient?.id;

    // Fetch (or refetch) the patient's saved cards. Factored out of the
    // load effect so the "add a card" popup can refresh the list and
    // auto-select the freshly-vaulted card on success. ``preferCardId``
    // pins the selection to a specific card when provided (the new one);
    // otherwise we fall back to the patient's default / first card.
    const reloadCards = useCallback(async (preferCardId) => {
        if (!selectedPatientId) { setCards([]); setSelectedCardId(''); return []; }
        setCardsLoading(true);
        try {
            const resp = await listPatientPaymentMethods(practiceId, selectedPatientId);
            const list = (resp?.paymentMethods || resp?.data || []).filter(c => c.status === 'ACTIVE');
            setCards(list);
            setSelectedCardId(
                (preferCardId && list.some(c => c.id === preferCardId) ? preferCardId : null)
                || list.find(c => c.isDefault)?.id
                || list[0]?.id
                || '',
            );
            return list;
        } catch (e) {
            console.error(e);
            setCards([]);
            return [];
        } finally {
            setCardsLoading(false);
        }
    }, [selectedPatientId, practiceId]);

    useEffect(() => {
        if (!selectedPatientId) { setCards([]); setSelectedCardId(''); return; }
        let cancelled = false;
        setCardsLoading(true);
        (async () => {
            try {
                const resp = await listPatientPaymentMethods(practiceId, selectedPatientId);
                const list = (resp?.paymentMethods || resp?.data || []).filter(c => c.status === 'ACTIVE');
                if (!cancelled) {
                    setCards(list);
                    setSelectedCardId(list.find(c => c.isDefault)?.id || list[0]?.id || '');
                }
            } catch (e) {
                console.error(e);
                if (!cancelled) setCards([]);
            } finally {
                if (!cancelled) setCardsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedPatientId, practiceId]);

    // Look up any "live" plans the patient already owns so the staffer
    // can see them before clicking Create. We deliberately scope to
    // active / paused / defaulted — completed / cancelled plans are done
    // and shouldn't surface as a warning. Reset the per-patient ack
    // (``confirmDuplicate``) any time the patient changes so a stale
    // confirmation from a prior selection can't slip a new patient
    // through.
    useEffect(() => {
        setConfirmDuplicate(false);
        if (!selectedPatientId) { setExistingPlans([]); return; }
        let cancelled = false;
        setExistingPlansLoading(true);
        (async () => {
            try {
                const resp = await listPaymentPlans(practiceId, {
                    patientId: selectedPatientId,
                    statuses: ['active', 'paused', 'defaulted'],
                });
                if (!cancelled) {
                    setExistingPlans(resp?.success ? (resp.data || []) : []);
                }
            } catch (e) {
                console.error('[EnrollPlanModal] existing-plans lookup failed:', e);
                if (!cancelled) setExistingPlans([]);
            } finally {
                if (!cancelled) setExistingPlansLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedPatientId, practiceId]);

    // Computed installment amount (display-only). Backend re-validates.
    const totalCents = Math.round((parseFloat(totalDollars) || 0) * 100);
    const installmentN = parseInt(installments, 10) || 0;
    const installmentCents = installmentN > 0
        ? Math.floor(totalCents / installmentN)
        : 0;
    const remainder = totalCents - (installmentCents * installmentN);

    const canSubmit = !!(
        patient && name.trim() && totalCents > 0 &&
        installmentN >= 2 && installmentCents > 0 &&
        startDate && selectedCardId && !submitting
    );

    const submit = async ({ forceConfirm = false } = {}) => {
        if (!canSubmit) return;
        // Soft-block: if the patient is already on at least one live
        // plan, route through the duplicate-confirm modal first. The
        // backend has no uniqueness check, so this is the only place
        // we ask the staffer "did you really mean to enroll twice?"
        // ``forceConfirm`` is the post-confirmation re-entry from the
        // ConfirmModal — it has to be a parameter rather than reading
        // state because we trigger this synchronously inside the
        // confirm handler, before React has flushed
        // ``setConfirmDuplicate(true)``.
        if (existingPlans.length > 0 && !confirmDuplicate && !forceConfirm) {
            setShowDuplicateConfirm(true);
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            const payload = {
                practiceId,
                patientId: selectedPatientId,
                patientName: patient.name || '',
                patientPhone: patient.phoneNumber || '',
                patientEmail: patient.email || '',
                name: name.trim(),
                description: description.trim(),
                totalAmountCents: totalCents,
                installments: installmentN,
                installmentAmountCents: installmentCents,
                startDate,
                paymentMethodVaultId: selectedCardId,
            };
            const res = await createPaymentPlan(payload);
            if (res?.ok && res?.data?.success !== false) {
                // Forward both the inline-charge outcome (for plans
                // dated today, or staff-forced) and the
                // invoice-provisioned outcome (for future-dated plans
                // — invoice is minted now, charge waits for cron).
                // Exactly one is populated at a time.
                onCreated({
                    firstCharge: res.data.firstCharge,
                    firstInvoice: res.data.firstInvoice,
                });
            } else {
                setError(res?.data?.error || res?.data?.code || 'Failed to create plan');
                setSubmitting(false);
            }
        } catch (e) {
            setError(String(e?.message || e));
            setSubmitting(false);
        }
    };

    // Card-aware surcharge preview. Mirrors compute_surcharge so what we
    // show here matches what the cron will actually charge — but we
    // explicitly note the value is recomputed per installment from the
    // practice's *current* settings, so a future toggle change applies
    // automatically without re-enrolling the plan.
    const selectedCard = cards.find(c => c.id === selectedCardId) || null;
    const cardFundingType = (selectedCard?.card?.type || '').toUpperCase();
    const surchargePreview = useMemo(() => (
        computeSurchargePreview(installmentCents, selectedCard, practiceCfg)
    ), [installmentCents, selectedCard, practiceCfg]);

    const surchargeBlock = useMemo(() => {
        if (!practiceCfg) return null;
        // Surcharging off: patients pay exactly the installment amount, so
        // there's nothing card-specific to preview — hide the block entirely.
        if (!practiceCfg.surchargeEnabled) return null;
        // Practice has surcharging on. Branch on the picked card, since
        // debit cards never surcharge regardless of practice settings.
        if (!selectedCard) {
            return {
                tone: 'info',
                title: 'Credit cards are surcharged; debit cards are not',
                body: `Credit-card installments will include a surcharge of up to ${practiceCfg.surchargePctCap || 3}%. Debit cards pay exactly the installment amount.`,
            };
        }
        if (cardFundingType === 'DEBIT') {
            return {
                tone: 'success',
                title: 'Debit card — no surcharge',
                body: 'Patient pays exactly the installment amount each month.',
            };
        }
        if (cardFundingType === 'CREDIT' && surchargePreview) {
            const fmt = (c) => `$${(c / 100).toFixed(2)}`;
            const pctStr = `${surchargePreview.pct % 1 === 0 ? surchargePreview.pct.toFixed(0) : surchargePreview.pct}`;
            return {
                tone: 'info',
                title: `Credit card — ${pctStr}% surcharge per installment`,
                body: `Patient pays ${fmt(surchargePreview.totalCents)} each month (${fmt(installmentCents)} base + ${fmt(surchargePreview.surchargeCents)} surcharge). The rate is read live from practice settings at each charge, so any future change to the surcharge configuration applies on the next installment.`,
            };
        }
        // Credit but missing brand / registered state / etc. — backend
        // would refuse to surcharge, so the patient pays base.
        return {
            tone: 'neutral',
            title: 'No surcharge will apply',
            body: 'This card does not meet the practice\'s surcharge eligibility rules; patient pays exactly the installment amount.',
        };
    }, [practiceCfg, selectedCard, cardFundingType, surchargePreview, installmentCents]);

    return (
        <ModalShell title="Enroll patient in payment plan" onClose={onClose} wide>
            {/* Patient picker */}
            <FormSection label="Patient">
                {!patient ? (
                    <>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by name, phone, or ID"
                            autoFocus
                            style={inputStyle}
                        />
                        {searching && (
                            <div style={{ marginTop: 8, color: '#6b716d', fontSize: '0.85rem' }}>
                                Searching…
                            </div>
                        )}
                        {!!searchResults.length && (
                            <div style={{
                                marginTop: 8, maxHeight: 200, overflow: 'auto',
                                border: '1px solid #e3e7e4', borderRadius: 6,
                            }}>
                                {searchResults.map(p => (
                                    <div
                                        key={p.id}
                                        onClick={() => { setPatient(p); setSearchResults([]); setSearchTerm(''); }}
                                        style={{
                                            padding: '10px 12px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #eef0ee',
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f7f8f7'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                    >
                                        <div data-phi style={{ fontWeight: 500, color: '#1a1d1b' }}>
                                            {p.name || '(no name)'}
                                        </div>
                                        <div data-phi style={{ fontSize: '0.8rem', color: '#8a908c' }}>
                                            ID: {p.patientId || p.id}
                                            {p.phoneNumber ? ` · ${p.phoneNumber}` : ''}
                                            {p.email ? ` · ${p.email}` : ''}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    <div style={{
                        padding: '10px 12px', background: '#f7f8f7',
                        border: '1px solid #e3e7e4', borderRadius: 6,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                        <div>
                            <div data-phi style={{ fontWeight: 500, color: '#1a1d1b' }}>
                                {patient.name || '(no name)'}
                            </div>
                            <div data-phi style={{ fontSize: '0.8rem', color: '#8a908c' }}>
                                ID: {patient.patientId || patient.id}
                                {patient.phoneNumber ? ` · ${patient.phoneNumber}` : ''}
                            </div>
                        </div>
                        <button
                            onClick={() => { setPatient(null); setCards([]); setSelectedCardId(''); setShowAddCardForm(false); }}
                            style={linkButtonStyle}
                        >Change</button>
                    </div>
                )}
            </FormSection>

            {/* Existing-plans warning. Renders under the patient picker
                as soon as we know the patient owns a live plan. The
                actual gate fires inside ``submit`` via
                ``showDuplicateConfirm`` — this banner is the heads-up
                so the staffer can self-correct before filling out the
                whole form. */}
            {patient && !existingPlansLoading && existingPlans.length > 0 && (
                <div style={{
                    padding: '12px 14px',
                    background: '#fef9f1',
                    border: '1px solid #f3d9ae',
                    borderRadius: 8,
                    color: '#a8520c',
                    fontSize: '0.85rem',
                    marginBottom: 14,
                    display: 'flex', gap: 10, alignItems: 'flex-start',
                }}>
                    <div style={{ fontSize: '1.05rem', lineHeight: 1.2 }}>⚠️</div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                            {existingPlans.length === 1
                                ? 'This patient already has 1 active plan'
                                : `This patient already has ${existingPlans.length} active plans`}
                        </div>
                        <div style={{ marginBottom: 6 }}>
                            Enrolling them again will create a second plan that charges
                            independently. Make sure that's intentional — most of the time
                            you want to update the existing plan instead.
                        </div>
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                            {existingPlans.map(p => (
                                <li key={p.id} style={{
                                    padding: '6px 0',
                                    borderTop: '1px solid #f3d9ae',
                                    color: '#a8520c',
                                }}>
                                    <span style={{ fontWeight: 600 }}>
                                        {p.name || 'Payment plan'}
                                    </span>
                                    {' · '}
                                    {formatCurrencyCents(p.installmentAmountCents || 0)}/mo
                                    {' · '}
                                    {Number(p.installmentsCompleted) || 0}/{Number(p.installments) || 0} done
                                    {' · '}
                                    <span style={{ textTransform: 'capitalize' }}>{p.status}</span>
                                    {p.nextChargeDate && p.status === 'active' && (
                                        <> · next {p.nextChargeDate}</>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* Plan name + description */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
                <FormSection label="Plan name *">
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Invisalign treatment"
                        style={inputStyle}
                    />
                </FormSection>
                <FormSection label="Description">
                    <input
                        type="text"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Internal note (optional)"
                        style={inputStyle}
                    />
                </FormSection>
            </div>

            {/* Money */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <FormSection label="Total amount *">
                    <div style={{ position: 'relative' }}>
                        <span style={{
                            position: 'absolute', left: 10, top: 9, color: '#8a908c',
                        }}>$</span>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={totalDollars}
                            onChange={(e) => setTotalDollars(e.target.value)}
                            placeholder="2400.00"
                            style={{ ...inputStyle, paddingLeft: 22 }}
                        />
                    </div>
                </FormSection>
                <FormSection label="Installments *">
                    <input
                        type="number"
                        min="2"
                        value={installments}
                        onChange={(e) => setInstallments(e.target.value)}
                        style={inputStyle}
                    />
                </FormSection>
                <FormSection label="Per installment">
                    <div style={{
                        ...inputStyle, background: '#f7f8f7', color: '#1a1d1b',
                        display: 'flex', alignItems: 'center',
                    }}>
                        {installmentCents > 0
                            ? formatCurrencyCents(installmentCents)
                            : '—'}
                        {remainder > 0 && installmentCents > 0 && (
                            <span style={{ marginLeft: 6, color: '#8a908c', fontSize: '0.8rem' }}>
                                (rounding ±${(remainder / 100).toFixed(2)})
                            </span>
                        )}
                    </div>
                </FormSection>
            </div>

            {/* Start date */}
            <FormSection label="Start date *">
                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ ...inputStyle, maxWidth: 200 }}
                />
                <div style={{ marginTop: 6, fontSize: '0.8rem', color: '#8a908c' }}>
                    The first invoice is generated as soon as you click Create — no matter how
                    far out this date is. The card on file will be charged automatically on
                    this date, but the patient can pay early via their invoice link, or staff
                    can charge it now from the Invoices tab. Subsequent installments run monthly
                    on this anchor day.
                </div>
            </FormSection>

            {/* Card selection */}
            <FormSection label="Card on file *">
                {!patient ? (
                    <div style={{ padding: 12, color: '#8a908c', fontSize: '0.9rem',
                                  background: '#f7f8f7', borderRadius: 6 }}>
                        Select a patient first.
                    </div>
                ) : cardsLoading ? (
                    <div style={{ padding: 12, color: '#6b716d' }}>Loading cards…</div>
                ) : !cards.length ? (
                    <div style={{ padding: 12, color: '#6b716d', background: '#f7f8f7',
                                  border: '1px solid #e3e7e4', borderRadius: 6,
                                  fontSize: '0.9rem', display: 'flex', flexDirection: 'column',
                                  alignItems: 'flex-start', gap: 10 }}>
                        <div>This patient has no active saved cards yet.</div>
                        <button
                            type="button"
                            onClick={() => setShowAddCardForm(true)}
                            style={{
                                padding: '8px 14px', background: '#1f5a3d', color: '#fff',
                                border: 'none', borderRadius: 6, fontSize: '0.85rem',
                                fontWeight: 600, cursor: 'pointer',
                            }}
                        >
                            + Add a card
                        </button>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div>
                            <button
                                type="button"
                                onClick={() => setShowAddCardForm(true)}
                                style={{
                                    padding: '6px 12px', background: 'white', color: '#1f5a3d',
                                    border: '1px solid #1f5a3d', borderRadius: 6, fontSize: '0.85rem',
                                    fontWeight: 600, cursor: 'pointer',
                                }}
                            >
                                + Add a card
                            </button>
                        </div>
                        {cards.map(c => (
                            <label key={c.id} style={{
                                display: 'flex', gap: 12, alignItems: 'center', padding: '10px 12px',
                                border: '1px solid', borderColor: selectedCardId === c.id ? '#1f5a3d' : '#e3e7e4',
                                borderRadius: 8, cursor: 'pointer',
                                background: selectedCardId === c.id ? '#f3f7fb' : 'white',
                            }}>
                                <input
                                    type="radio"
                                    checked={selectedCardId === c.id}
                                    onChange={() => setSelectedCardId(c.id)}
                                />
                                <div style={{ flex: 1 }}>
                                    <CardLabel
                                        card={c}
                                        baseAmountCents={installmentCents}
                                        practiceCfg={practiceCfg}
                                    />
                                </div>
                            </label>
                        ))}
                    </div>
                )}
            </FormSection>

            {/* Surcharge preview — card-aware, recomputed live as the
                staff member edits the amount or picks a different card. */}
            {surchargeBlock && (() => {
                const palette = {
                    info:    { bg: '#f3f7fb', border: '#c5d8ea', color: '#1f4e79', icon: '💳' },
                    success: { bg: '#e7f4ec', border: '#c6e3d0', color: '#1d6b40', icon: '✓' },
                    neutral: { bg: '#f7f8f7', border: '#e3e7e4', color: '#3c4540', icon: '•' },
                };
                const p = palette[surchargeBlock.tone] || palette.neutral;
                return (
                    <div style={{
                        padding: '12px 14px', background: p.bg, border: `1px solid ${p.border}`,
                        borderRadius: 8, color: p.color, fontSize: '0.85rem',
                        marginBottom: 12,
                        display: 'flex', gap: 10, alignItems: 'flex-start',
                    }}>
                        <div style={{ fontSize: '1.05rem', lineHeight: 1.2 }}>{p.icon}</div>
                        <div>
                            <div style={{ fontWeight: 600, marginBottom: 2 }}>
                                {surchargeBlock.title}
                            </div>
                            <div>{surchargeBlock.body}</div>
                        </div>
                    </div>
                );
            })()}

            {error && (
                <div style={{
                    padding: '10px 12px', background: '#fef5f4', border: '1px solid #f5c6c1',
                    borderRadius: 6, color: '#912018', marginBottom: 12,
                }}>{error}</div>
            )}

            <ModalFooter>
                <ActionButton label="Cancel" onClick={onClose} disabled={submitting}/>
                <ActionButton
                    label="Create plan"
                    primary
                    onClick={submit}
                    disabled={!canSubmit}
                    busy={submitting}
                />
            </ModalFooter>

            {showDuplicateConfirm && (
                <ConfirmModal
                    title="Enroll in another plan?"
                    body={(
                        <div>
                            <div style={{ marginBottom: 8 }}>
                                {patient?.name ? (
                                    <><b data-phi>{patient.name}</b> already has </>
                                ) : 'This patient already has '}
                                {existingPlans.length === 1
                                    ? '1 active plan'
                                    : `${existingPlans.length} active plans`}
                                . Creating this plan will run it
                                <i> in addition to </i>
                                the existing one(s) — both will charge independently each
                                month.
                            </div>
                            <div style={{ color: '#6b716d', fontSize: '0.85rem' }}>
                                If you meant to change the amount or card on an existing
                                plan, click Cancel and edit it from the plan detail
                                drawer instead.
                            </div>
                        </div>
                    )}
                    confirmLabel="Yes, enroll in another plan"
                    onClose={() => setShowDuplicateConfirm(false)}
                    onConfirm={() => {
                        setShowDuplicateConfirm(false);
                        // Latch the ack so the Create button works
                        // without re-confirming if the staffer cancels
                        // and clicks again. Patient-change effect
                        // resets it for any subsequent selection.
                        setConfirmDuplicate(true);
                        // Re-enter submission immediately with an
                        // explicit override so we don't have to wait
                        // for setConfirmDuplicate to propagate through
                        // the closure.
                        submit({ forceConfirm: true });
                    }}
                />
            )}

            {/* Add-card popup — vault a card for this patient without
                leaving enrollment. On success we refresh the saved-card
                list and auto-select the freshly-added card so the staffer
                can immediately continue creating the plan. */}
            {showAddCardForm && patient && (
                <ModalShell
                    title="Add a card"
                    onClose={() => setShowAddCardForm(false)}
                >
                    <StaffAddCardForm
                        practiceId={practiceId}
                        patientId={selectedPatientId}
                        hasExistingCards={cards.length > 0}
                        onSaved={async (savedRow) => {
                            setShowAddCardForm(false);
                            showToast?.(
                                `Card saved: ${(savedRow?.card?.brand || 'Card').replace(/_/g, ' ')} •• ${savedRow?.card?.last4 || ''}`,
                            );
                            await reloadCards(savedRow?.id);
                        }}
                        onCancel={() => setShowAddCardForm(false)}
                    />
                </ModalShell>
            )}
        </ModalShell>
    );
};

// ============================================================================
// Generic modal primitives
// ============================================================================

const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #d6dbd7',
    borderRadius: 6,
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
};

const linkButtonStyle = {
    background: 'transparent',
    border: 'none',
    color: '#1f5a3d',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
};

const FormSection = ({ label, children }) => (
    <div style={{ marginBottom: 14 }}>
        <label style={{
            display: 'block', marginBottom: 4, color: '#3c4540',
            fontSize: '0.85rem', fontWeight: 500,
        }}>{label}</label>
        {children}
    </div>
);

const ModalShell = ({ title, children, onClose, wide }) => (
    <div
        onClick={onClose}
        style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 950,
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            padding: 20,
        }}
    >
        <div
            onClick={(e) => e.stopPropagation()}
            style={{
                background: 'white',
                borderRadius: 12,
                width: wide ? 'min(640px, 100%)' : 'min(480px, 100%)',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
            }}
        >
            <div style={{
                padding: '18px 20px',
                borderBottom: '1px solid #e3e7e4',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                textAlign: 'left',
            }}>
                <h2 style={{ margin: 0, color: '#1a1d1b', fontSize: '1.1rem' }}>{title}</h2>
                <button
                    onClick={onClose}
                    aria-label="Close"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                             fontSize: '1.4rem', color: '#8a908c', lineHeight: 1 }}
                >×</button>
            </div>
            {/* textAlign: 'left' overrides the CRA-default
                ``.App { text-align: center }`` so every label / form
                section / paragraph inside the modal renders flush-left
                without each child having to set it individually. */}
            <div style={{ padding: 20, textAlign: 'left' }}>{children}</div>
        </div>
    </div>
);

const ModalFooter = ({ children }) => (
    <div style={{
        marginTop: 16,
        paddingTop: 16,
        borderTop: '1px solid #eef0ee',
        display: 'flex', justifyContent: 'flex-end', gap: 8,
    }}>{children}</div>
);

const ConfirmModal = ({ title, body, confirmLabel, confirmDanger, onConfirm, onClose }) => (
    <ModalShell title={title} onClose={onClose}>
        <div style={{ color: '#3c4540', fontSize: '0.95rem' }}>{body}</div>
        <ModalFooter>
            <ActionButton label="Cancel" onClick={onClose}/>
            <ActionButton
                label={confirmLabel || 'Confirm'}
                primary={!confirmDanger}
                danger={confirmDanger}
                onClick={onConfirm}
            />
        </ModalFooter>
    </ModalShell>
);

// Reused by the Invoices tab's "View plan details" on plan installments.
export { PlanDetailDrawer };
export default PaymentPlansTab;
